import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { useId } from 'react';

import { COLORS, useResponsive } from '@/shared/lib';
import { ChartContainer, ChartTooltip, Typography } from '@/shared/ui';

import { chartConfig } from '../model/chart-config';
import { ExchangeRates } from '../model/types';
import {
  CustomActiveDot,
  CustomDot,
  CustomYAxisTick,
  ExchangeRateTooltip,
} from './chart-utilities';

interface ExchangeChartProps {
  exchangeRates: ExchangeRates;
}

export const ExchangeChart = ({ exchangeRates }: ExchangeChartProps) => {
  const gradientId = useId().replace(/:/g, '-');
  const { rates } = exchangeRates;
  const { isDesktop } = useResponsive();
  if (!rates.length) {
    return (
      <div className="flex h-[180px] w-full items-center justify-center">
        <Typography color="text-base-100" variant="14-r">
          No exchange rates yet
        </Typography>
      </div>
    );
  }

  const values = rates.map((rate) => rate.rate);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const todayValue = rates[rates.length - 1].rate;
  const firstValue = rates[0].rate;
  const isPositiveTrend = todayValue >= firstValue;
  const chartColor = isPositiveTrend ? COLORS['bg-brand-500'] : '#FF2A3B';
  const yAxisDomain = [minValue - 0.04, maxValue + 0.03];
  const firstDate = new Date(rates[0].date);
  const formattedDate = firstDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="flex h-full w-full flex-col">
      <ChartContainer
        config={chartConfig}
        className="h-[180px] w-full px-4 md:h-[229px] md:pl-0"
      >
        <ResponsiveContainer width="100%" height={isDesktop ? 229 : 180}>
          <AreaChart
            accessibilityLayer
            data={rates}
            height={isDesktop ? 229 : 180}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={chartColor}
                  stopOpacity={0.4}
                />
                <stop
                  offset="100%"
                  stopColor={chartColor}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={false}
              hide
            />
            <YAxis
              domain={yAxisDomain}
              width={40}
              orientation="right"
              tickFormatter={(value: number) => value.toFixed(3)}
              ticks={[minValue, todayValue, maxValue]}
              tickMargin={11}
              tickCount={3}
              tickLine={false}
              axisLine={false}
              tick={<CustomYAxisTick todayValue={todayValue} />}
              className="absolute text-base font-normal leading-[22px]"
            />
            <ChartTooltip
              cursor={false}
              content={<ExchangeRateTooltip currency={exchangeRates.to} />}
            />
            <Area
              dataKey="rate"
              type="natural"
              fill={`url(#${gradientId})`}
              stroke={chartColor}
              strokeWidth={3}
              dot={<CustomDot rates={rates} />}
              activeDot={<CustomActiveDot />}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartContainer>
      <div className="mt-2 flex w-full items-center justify-between pl-4 pr-[60px] md:pl-2">
        <Typography color="text-base-100" variant="16-r">
          {formattedDate}
        </Typography>
        <Typography color="text-base-100" variant="16-r">
          Now
        </Typography>
      </div>
    </div>
  );
};
